import React, { createContext, useContext, useState, useCallback } from 'react';
import { CheckCircle, AlertCircle, X } from 'lucide-react';

type NotificationType = 'success' | 'error';

interface Notification {
  id: string;
  message: string;
  type: NotificationType;
}

interface NotificationContextType {
  showNotification: (message: string, type?: NotificationType) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const showNotification = useCallback((message: string, type: NotificationType = 'success') => {
    const id = Math.random().toString(36).substr(2, 9);
    setNotifications((prev) => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    }, 3000);
  }, []);

  const removeNotification = (id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  return (
    <NotificationContext.Provider value={{ showNotification }}>
      {children}
      <div className="fixed top-24 right-4 z-[999] flex flex-col gap-2 w-full max-w-[240px] md:max-w-xs pointer-events-none">
        {notifications.map((n) => (
          <div
            key={n.id}
            className={`pointer-events-auto flex items-center justify-between p-3 rounded-xl shadow-2xl border animate-[slideInRight_0.3s_ease-out] backdrop-blur-md ${
              n.type === 'success'
                ? 'bg-stone-900/90 border-gold-500/30 text-white'
                : 'bg-red-950/90 border-red-500/30 text-white'
            }`}
          >
            <div className="flex items-center gap-2">
              {n.type === 'success' ? (
                <CheckCircle className="text-gold-500 shrink-0" size={14} />
              ) : (
                <AlertCircle className="text-red-400 shrink-0" size={14} />
              )}
              <p className="text-[9px] font-bold uppercase tracking-wider leading-tight">{n.message}</p>
            </div>
            <button
              onClick={() => removeNotification(n.id)}
              className="ml-2 p-1 hover:bg-white/10 rounded-full transition-colors shrink-0"
            >
              <X size={12} />
            </button>
          </div>
        ))}
      </div>
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) throw new Error('useNotification must be used within NotificationProvider');
  return context;
};